import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id';

export default function LoginSectionSignIn1() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Load Google Sign-In script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getApiBase = () => {
        try {
            const envBase = process.env.REACT_APP_API_BASE_URL;
            if (envBase && envBase.trim()) {
                let base = envBase.trim().replace(/\/$/, '');
                if (typeof window !== 'undefined' && window.location.protocol === 'https:' && base.startsWith('http://')) {
                    base = base.replace('http://', 'https://');
                }
                return base;
            }
        } catch (_) {}
        return window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const apiBase = getApiBase();
            const response = await fetch(`${apiBase}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    username: formData.email,
                    password: formData.password
                })
            });

            const data = await response.json();

            if (response.ok) {
                // Store the JWT token
                localStorage.setItem('access_token', data.access_token);
                
                // Check subscription status
                await checkSubscriptionAndRedirect(data.access_token, apiBase);
            } else {
                setError(data.detail || 'Login failed. Please check your credentials.');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setError('');
            
            if (!window.google) {
                setError('Google Sign-In is not loaded. Please refresh the page.');
                return;
            }
            
            // Use the OAuth popup flow for account selection
            window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                callback: async (response) => {
                    if (response.access_token) {
                        try {
                            // Get user info from Google
                            const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`);
                            const userInfo = await userInfoResponse.json();
                            
                            // Create a credential-like object for our backend
                            const credential = {
                                email: userInfo.email,
                                name: userInfo.name,
                                google_id: userInfo.id,
                                picture: userInfo.picture
                            };
                            
                            // Call our backend
                            const apiBase = getApiBase();
                            const result = await fetch(`${apiBase}/auth/google-oauth`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(credential),
                            });

                            const data = await result.json();

                            if (result.ok) {
                                // Store the JWT token
                                localStorage.setItem('access_token', data.access_token);
                                
                                // Check subscription status
                                await checkSubscriptionAndRedirect(data.access_token, apiBase);
                            } else {
                                setError(data.detail || 'Google sign-in failed. Please try again.');
                            }
                        } catch (error) {
                            console.error('Google OAuth error:', error);
                            setError('Google sign-in failed. Please try again.');
                        }
                    }
                }
            }).requestAccessToken();
            
        } catch (error) {
            console.error('Google Sign-In initialization error:', error);
            setError('Google Sign-In failed. Please try again.');
        }
    };

    const checkSubscriptionAndRedirect = async (accessToken, apiBase) => {
        try {
            if (!apiBase) {
                // No API base configured in non-localhost environment; proceed to app
                navigate('/dashboard');
                return;
            }

            const subResponse = await fetch(`${apiBase}/subscription`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            
            if (subResponse.ok) {
                const subData = await subResponse.json();
                console.log('🔍 Login - Subscription check:', subData);
                
                // Normalize and broaden acceptable states
                const normalizedStatus = (subData?.subscription_status || '').toLowerCase();
                const hasActivePlan = (normalizedStatus === 'active' || normalizedStatus === 'trialing');
                
                if (hasActivePlan) {
                    console.log('✅ User has active subscription - redirecting to dashboard');
                    navigate('/dashboard');
                } else {
                    console.log('👤 User needs subscription - redirecting to plan selection');
                    navigate('/plan-selection');
                }
            } else {
                // If subscription endpoint returns an error, proceed to dashboard and let in-app checks handle UX
                console.log('⚠️ Subscription check failed - proceeding to dashboard');
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('Subscription check error:', error);
            // Network or CORS error; proceed to app and let in-app checks handle gracefully
            navigate('/dashboard');
        }
    };

    return (
        <React.Fragment>
            <>
                <section className="relative py-20 min-h-screen bg-white dark:bg-gray-900">
                    <img className="hidden lg:block absolute top-0 left-0 mt-16" src="zeus-assets/icons/dots/blue-dot-left-bars.svg" alt="" />
                    <img className="hidden lg:block absolute bottom-0 right-0 mb-20" src="zeus-assets/icons/dots/yellow-dot-right-shield.svg" alt="" />
                    <div className="container px-4 mx-auto">
                        <div className="max-w-md mx-auto py-6 lg:py-12 px-4 lg:px-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-center">
                            <h3 className="mb-12 text-3xl font-semibold font-heading">Sign in</h3>
                            
                            {error && (
                                <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                    {error}
                                </div>
                            )}
                            
                            <form onSubmit={handleEmailLogin}>
                                <div className="relative flex flex-wrap mb-6">
                                    <input 
                                        className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="e.g hello@shuffle.dev" 
                                        required
                                    />
                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs">Your email address</span>
                                </div>
                                <div className="relative flex flex-wrap mb-6">
                                    <input 
                                        className="relative mb-2 md:mb-0 w-full py-4 pl-4 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                                        type="password" 
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="******" 
                                        required
                                    />
                                    <span className="absolute top-0 left-0 ml-4 -mt-2 px-1 inline-block bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs">Password</span>
                                </div>
                                
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className={`w-full inline-block py-4 mb-4 text-sm text-white font-medium leading-normal rounded transition duration-200 text-center ${
                                        loading 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-red-400 hover:bg-red-300'
                                    }`}
                                >
                                    {loading ? 'Signing In...' : 'Sign In'}
                                </button>
                                
                                <div className="mb-6">
                                    <div className="flex items-center justify-center">
                                        <hr className="w-full border-gray-300" />
                                        <span className="px-3 text-gray-500 text-sm">or</span>
                                        <hr className="w-full border-gray-300" />
                                    </div>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    className="w-full flex items-center justify-center py-4 mb-4 text-sm text-gray-700 dark:text-gray-900 font-medium leading-normal bg-white border border-gray-300 rounded transition duration-200 text-center hover:bg-gray-50"
                                >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    Continue with Google
                                </button>
                                
                                <div className="text-center">
                                    <a className="inline-block pb-2 mb-2 text-sm font-semibold text-blue-600 dark:text-blue-300 hover:underline" href="#">Forgot password?</a>
                                    <span className="block text-sm text-gray-700 dark:text-gray-200">Don't have an account? <Link to="/register" className="text-blue-600 dark:text-blue-300 hover:underline">Sign up</Link></span>
                                </div>
                            </form>
                        </div>
                    </div>
                </section>
            </>
        </React.Fragment>
    );
}

